import { useEffect, useRef } from 'react';

import { useCFPValues } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { useDidMount, useWillUnmount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { Box, Button, ComposableTypography } from '@bcp-nextgen-dx-component-factory/design-system';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { Captcha, ChipsList, CollapsibleBlock, InvalidDataAlert, Sms, Token } from '@components';
import { LoggerHelper, UIHelper } from '@helpers';
import { useLoginForm, useModal, useTranslation } from '@hooks';
import { useFetchAccountSafe } from '@services';
import { TokenInputs, useLoginStore, useServicesStore } from '@store';
import { SafeCredentialTypeEnum } from '@types';

const resources = {
	title: 'screens.login.form.safe.title',
	subtitle: 'screens.login.form.safe.subtitle',
	button: {
		continue: 'screens.login.button.continue',
	},
};

export interface SafeBlockProps {
	isActive?: boolean;
	onSubmit?: (withCertificate?: boolean, safeCredentialType?: SafeCredentialTypeEnum) => void;
}

export const SafeBlock = ({ isActive, onSubmit }: SafeBlockProps) => {
	const { t } = useTranslation();
	const { getToken } = useCFPValues();
	const genericErrorModal = useModal('genericError');
	const { fetch: fetchAccountSafe } = useFetchAccountSafe();
	const { safeCredentialType, positionsToAsk } = useServicesStore((store) => store.accountLogin) || {};
	const { mobileNumber } = useServicesStore((store) => store.accountSafe) || {};
	const intervalId = useRef<NodeJS.Timer>(null);
	const { safe, setSafeFetchCount, setSafeSelectedChip, decrementSafeSmsCounter, resetSafeSmsCounter } =
		useLoginStore();
	const { sms, token, multichannel, showCaptcha, captchaValue, onContinueClick, setState } = useLoginForm();

	/* start counter, if applicable */
	useDidMount(() => {
		if (safe?.fetchCount?.sms) {
			intervalId.current = setInterval(() => {
				decrementSafeSmsCounter();
			}, 1000);
		}
	});

	useWillUnmount(() => {
		intervalId.current && clearInterval(intervalId.current);
	});

	/* stop counter if reaches 0 */
	useEffect(() => {
		safe?.smsCounter === 0 && intervalId.current && clearInterval(intervalId.current);
	}, [safe?.smsCounter]);

	const handleSmsChipClick = async () => {
		try {
			if (!safe?.fetchCount?.sms) {
				await requestSms();
			}

			setSafeSelectedChip(SafeCredentialTypeEnum.Sms);
		} catch {}
	};

	const handleTokenChipClick = async () => {
		try {
			if (!safe?.fetchCount?.token) {
				await requestToken();
			}

			setSafeSelectedChip(SafeCredentialTypeEnum.Token);
		} catch {}
	};

	const handleContinueClick = () => {
		onContinueClick(onSubmit, { withCertificate: false, safeCredentialType: safe?.selectedChip });
	};

	const handleCaptchaChange = (value: string) => {
		setState({ form: { captchaValue: value } });
	};

	const requestSms = async () => {
		try {
			const { data } = await fetchAccountSafe({ credentialTypeId: SafeCredentialTypeEnum.Sms });

			if (!data?.success || !data?.mobileNumber) {
				LoggerHelper.error('App', 'SafeBlock > Error fetching /account/safe > no success or no mobileNumber', {
					...data,
				});
				throw new Error();
			}

			setSafeFetchCount({ sms: (safe?.fetchCount?.sms || 0) + 1 });
			resetSafeSmsCounter();

			intervalId.current = setInterval(() => {
				decrementSafeSmsCounter();
			}, 1000);
		} catch {
			genericErrorModal.show();
			return Promise.reject();
		}
	};

	const requestToken = async () => {
		try {
			// Do not call accountSafe for Token for now. Token I and Token A types need to be clarified...
			// await fetchAccountSafe({ credentialTypeId: SafeCredentialTypeEnum.Token });
			setSafeFetchCount({ token: (safe?.fetchCount?.token || 0) + 1 });
		} catch {
			return Promise.reject();
		}
	};

	const openContentButtonDisabled = getOpenContentButtonDisabled(
		multichannel,
		positionsToAsk,
		showCaptcha,
		captchaValue,
		safe?.selectedChip,
		sms,
		token
	);
	const showChipsList = getShowChipsList(safeCredentialType, safe?.fetchCount);

	const openContentChildren = (
		<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(32) }}>
			<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(8) }}>
				<ComposableTypography
					text={t(resources.title)}
					baseProps={{
						appearance: getToken({
							default: { default: 'subtitle-200-02' },
						}),
					}}
					tags={{}}
				/>

				<ComposableTypography
					text={t(resources.subtitle)}
					baseProps={{
						appearance: getToken({
							default: { default: 'body-100-02' },
						}),
						colorToken: 'secondary-light',
					}}
					tags={{}}
				/>
			</Box>

			<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(24) }}>
				{showChipsList && (
					<ChipsList
						safeCredentialType={safeCredentialType}
						selectedChip={safe?.selectedChip}
						onChipClick={(chip) => {
							if (chip === SafeCredentialTypeEnum.Token) {
								handleTokenChipClick();
							} else {
								handleSmsChipClick();
							}
						}}
					/>
				)}
				{safe?.selectedChip === SafeCredentialTypeEnum.Sms && (
					<Sms
						phoneNumber={mobileNumber}
						value={sms}
						onValueChange={(value) => setState({ form: { sms: value } })}
						counter={safe.smsCounter}
						onLinkClick={requestSms}
					/>
				)}

				{safe?.selectedChip === SafeCredentialTypeEnum.Token && (
					<Token
						input1={{
							value: token?.input1,
							onChange: (value) =>
								setState({
									form: {
										token: {
											input1: value,
										},
									},
								}),
						}}
						input2={{
							value: token?.input2,
							onChange: (value) =>
								setState({
									form: {
										token: {
											input2: value,
										},
									},
								}),
						}}
					/>
				)}
			</Box>

			{showCaptcha && <Captcha value={captchaValue} onChange={handleCaptchaChange} />}

			<InvalidDataAlert />

			<Box styles={{ alignSelf: 'start' }}>
				<Button
					size="large"
					type="submit"
					endContent={ArrowRight.Outline}
					disabled={openContentButtonDisabled}
					onPress={handleContinueClick}
				>
					{t(resources.button.continue)}
				</Button>
			</Box>
		</Box>
	);

	return (
		<CollapsibleBlock
			isOpen={isActive}
			openContent={{
				children: openContentChildren,
			}}
		/>
	);
};

const getShowChipsList = (
	safeCredentialType: SafeCredentialTypeEnum | null | undefined,
	fetchCount?: { sms?: number; token?: number }
) => {
	let showChipsList = true;
	if (
		(safeCredentialType === SafeCredentialTypeEnum.Sms && !!fetchCount?.sms) ||
		(safeCredentialType === SafeCredentialTypeEnum.Token && !!fetchCount?.token)
	) {
		showChipsList = false;
	}
	return showChipsList;
};

const getOpenContentButtonDisabled = (
	multichannel: string | undefined,
	positionsToAsk: number[] | null | undefined,
	showCaptcha: boolean,
	captchaValue: string,
	selectedChip: SafeCredentialTypeEnum | undefined,
	sms: string | undefined,
	token: TokenInputs | undefined
) => {
	let openContentButtonDisabled = true;
	const multichannelDisabled = !multichannel || multichannel.length < (positionsToAsk?.length || 0);
	const captchaValidation = showCaptcha && !captchaValue;
	if (selectedChip === SafeCredentialTypeEnum.Sms) {
		openContentButtonDisabled = !sms || sms.length < 7 || multichannelDisabled || captchaValidation;
	} else if (selectedChip === SafeCredentialTypeEnum.Token) {
		openContentButtonDisabled =
			!token || !token.input1?.length || !token.input2?.length || multichannelDisabled || captchaValidation;
	}
	return openContentButtonDisabled;
};
